import { Children, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Select,
  SelectItem,
  Spinner,
  Tab,
  Tabs,
  Tooltip
} from '@nextui-org/react';
import LineChart from '../components/LineChart';
import AgencyBillingSection from '../components/AgencyBillingSection';
import DashboardSection from '../components/DashboardSection';
import { apiFetch } from '../lib/api';
import { loadDashboard, refreshDashboard, useDashboardStore } from '../lib/dashboardStore';
import { useAuth } from '../hooks/useAuth';

// Dashboard principal del backoffice. Una sola llamada a
// GET /api/backoffice/dashboard, que el Worker sirve desde datos ya
// calculados (stock cacheado en KV + snapshots diarios + flujos de Stripe
// acumulados por un cron): la respuesta tarda milisegundos. Los datos viven
// en un store compartido (lib/dashboardStore.js), así cambiar de pestaña y
// volver no recarga nada.

const PLAN_OPTIONS = [
  ['total', 'Total'],
  ['founders', 'Founders'],
  ['premium', 'Premium'],
  ['performance', 'Performance'],
  ['agencies', 'Agencias']
];

const PLAN_LABELS = { founders: 'Founders', premium: 'Premium', performance: 'Performance', agencies: 'Agencias' };

// Claves del snapshot/summary por plan. Las agencias (planes Rookie, Silver,
// Gold y Titanium) son otro contador: no entran en los totales de
// suscriptores ni de suscripciones (solo en su bloque y en la opción
// «Agencias» de las gráficas). Hasta nov-2026 se sumaban a Premium.
const CREATOR_PLANS = ['founders', 'premium', 'performance'];
const ACTIVE_KEYS = {
  founders: 'foundersActivePaying',
  premium: 'premiumsActivePaying',
  performance: 'performanceActivePaying',
  agencies: 'agenciesActivePaying'
};
const CANCELING_KEYS = {
  founders: 'foundersCanceling',
  premium: 'premiumsCanceling',
  performance: 'performanceCanceling',
  agencies: 'agenciesCanceling'
};
const PAST_DUE_KEYS = {
  founders: 'foundersPastDue',
  premium: 'premiumsPastDue',
  performance: 'performancePastDue',
  agencies: 'agenciesPastDue'
};

// "Suscriptores activos" tal y como lo cuenta el Inicio de Stripe: activas
// sin cancelacion programada + impagos en curso (past_due).
function stripeActiveValue(snapshot, plan) {
  return stockValue(snapshot, ACTIVE_KEYS, plan) + stockValue(snapshot, PAST_DUE_KEYS, plan);
}

const COMPARE_OPTIONS = [
  ['yesterday', 'Ayer'],
  ['d7', 'Hace 7 dias'],
  ['d30', 'Hace 30 dias']
];

const RANGE_OPTIONS = [
  ['14', '14 dias'],
  ['30', '30 dias'],
  ['90', '90 dias']
];

const COLORS = {
  active: '#3b82f6',
  trial: '#a855f7',
  cancel: '#f97316',
  ended: '#ef4444',
  newSubs: '#22c55e',
  renewed: '#14b8a6',
  registrations: '#6366f1',
  revenue: '#eab308'
};

const STALE_REFETCH_DELAY_MS = 6000;
const STALE_REFETCH_MAX = 4;

function n(value) {
  return Number(value || 0);
}

function fmt(value) {
  return n(value).toLocaleString('es-ES');
}

function formatDateTime(iso) {
  const d = new Date(iso || 0);
  return Number.isFinite(d.getTime()) && d.getTime() > 0 ? d.toLocaleString('es-ES') : '-';
}

function formatDay(day) {
  const d = new Date(`${day}T00:00:00`);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : day;
}

function formatAge(iso) {
  const ms = new Date(iso || 0).getTime();
  if (!ms) return '';
  const minutes = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (minutes < 1) return 'hace un momento';
  if (minutes < 60) return `hace ${minutes} min`;
  return `hace ${Math.round(minutes / 60)} h`;
}

// Total de creadores (sin agencias).
function sumKeys(source, keys) {
  return CREATOR_PLANS.reduce((acc, plan) => acc + n(source?.[keys[plan]]), 0);
}

// Desglose por plan de creador para las tarjetas de arriba.
function planBreakdown(source, keys, extra = null) {
  return CREATOR_PLANS.map((plan) => [PLAN_LABELS[plan], n(source?.[keys[plan]]) + (extra ? n(source?.[extra[plan]]) : 0)]);
}

// Valor de un plan (o el total de creadores) en un snapshot de stock.
function stockValue(snapshot, keys, plan) {
  if (!snapshot) return 0;
  return plan === 'total' ? sumKeys(snapshot, keys) : n(snapshot[keys[plan]]);
}

// Valor de un plan (o el total de creadores) en una fila de flujo
// { total, founders, ..., agencies }: altas, renovaciones, cancelaciones y
// facturación. Lo de agencias solo con plan = 'agencies' (y en su bloque).
function flowValue(row, plan) {
  if (!row) return 0;
  return plan === 'total' ? n(row.total) - n(row.agencies) : n(row[plan]);
}

function lastDays(rows, days) {
  return rows.slice(Math.max(0, rows.length - days));
}

function Delta({ value, baseline, badWhenUp = false, label }) {
  if (typeof baseline !== 'number') {
    return <span className="text-xs text-default-400">sin historico</span>;
  }
  const delta = value - baseline;
  const tone = delta === 0 ? 'text-default-500' : (delta > 0) !== badWhenUp ? 'text-success-600' : 'text-danger-600';
  const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '=';
  return (
    <span className={`text-xs font-medium ${tone}`}>
      {arrow} {delta > 0 ? `+${fmt(delta)}` : fmt(delta)}
      {label ? <span className="font-normal text-default-400"> {label}</span> : null}
    </span>
  );
}

// (i) con tooltip propio: la explicacion deja de ocupar sitio en la fila.
function InfoTip({ text }) {
  return (
    <Tooltip
      placement="bottom"
      delay={150}
      closeDelay={0}
      content={<div className="max-w-xs px-1 py-1 text-xs leading-5 text-default-600">{text}</div>}
      classNames={{ content: 'border border-default-200 bg-content1 shadow-xl' }}
    >
      <span
        className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full border border-default-300 text-[10px] font-semibold leading-none text-default-500 transition hover:border-default-500 hover:text-default-700"
        aria-label="Mas informacion"
      >
        i
      </span>
    </Tooltip>
  );
}

// Metrica de la fila principal: sin tarjeta, separada de sus vecinas por una
// linea vertical (divide-x del contenedor).
function KpiStat({ title, info, value, accent, delta, breakdown }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5 px-5 first:pl-0 last:pr-0">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-default-500">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: accent }} />
        <span className="truncate">{title}</span>
        {info && <InfoTip text={info} />}
      </div>
      <div className="text-3xl font-semibold leading-none">{fmt(value)}</div>
      <div>{delta}</div>
      {breakdown && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-default-500">
          {breakdown.map(([label, count]) => (
            <span key={label}>
              <span className="font-medium text-default-700">{fmt(count)}</span> {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Importes en céntimos por moneda (Stripe cobra en USD y EUR; no se
// convierten). { usd: 123456, eur: 7890 } → "$1.234,56 · €78,90".
const CURRENCY_SYMBOLS = { usd: '$', eur: '€' };

function formatMoney(cents, currency) {
  const amount = Number(cents || 0) / 100;
  const symbol = CURRENCY_SYMBOLS[currency] || `${String(currency || '').toUpperCase()} `;
  return `${symbol}${amount.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatMoneyMap(map) {
  const parts = Object.entries(map || {})
    .filter(([, cents]) => Number(cents) > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, cents]) => formatMoney(cents, currency));
  return parts.length ? parts.join(' · ') : '0';
}

function MoneyStat({ title, info, amounts, accent, detail }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5 px-5 first:pl-0 last:pr-0">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-default-500">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: accent }} />
        <span className="truncate">{title}</span>
        {info && <InfoTip text={info} />}
      </div>
      <div className="text-2xl font-semibold leading-none">{formatMoneyMap(amounts)}</div>
      {detail && <div className="text-xs text-default-500">{detail}</div>}
    </div>
  );
}

function MiniStat({ value, label, color }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="text-xl font-semibold leading-none" style={{ color }}>{fmt(value)}</span>
      <span className="text-xs text-default-500">{label}</span>
    </span>
  );
}

function PlanSwitch({ value, onChange, withTotal = true }) {
  const options = withTotal ? PLAN_OPTIONS : PLAN_OPTIONS.filter(([key]) => key !== 'total');
  return (
    <Select
      aria-label="Plan"
      size="sm"
      className="w-36"
      disallowEmptySelection
      selectedKeys={[value]}
      onSelectionChange={(keys) => onChange(String(Array.from(keys)[0] || 'total'))}
    >
      {options.map(([key, label]) => <SelectItem key={key}>{label}</SelectItem>)}
    </Select>
  );
}

// Reparte las graficas en filas de `perRow` (2 con 90 dias, 4 en el resto),
// cada fila con su linea superior y separadores verticales entre graficas.
function ChartRows({ perRow, children }) {
  const items = Children.toArray(children);
  const rows = [];
  for (let i = 0; i < items.length; i += perRow) rows.push(items.slice(i, i + perRow));
  return rows.map((row, index) => (
    <div
      key={index}
      className={`grid grid-cols-1 gap-y-8 border-t border-default-200 pt-6 xl:grid-cols-2 xl:divide-x xl:divide-default-200 ${perRow >= 4 ? '2xl:grid-cols-4' : ''}`}
    >
      {row}
    </div>
  ));
}

// Grafica sin tarjeta: titulo + total arriba, controles debajo. Las
// secciones de una misma fila se separan con lineas verticales.
function ChartSection({ title, subtitle, info, headline, headlineLabel, toolbar, children }) {
  return (
    <div className="flex min-w-0 flex-col px-6 first:pl-0 last:pr-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-semibold">
            <span>{title}</span>
            {info && <InfoTip text={info} />}
          </div>
          {subtitle && <div className="text-xs text-default-500">{subtitle}</div>}
        </div>
        {headline != null && (
          <div className="shrink-0 text-right">
            <div className="text-3xl font-semibold leading-none">{fmt(headline)}</div>
            <div className="mt-1 text-[11px] text-default-500">{headlineLabel}</div>
          </div>
        )}
      </div>
      <div className="mb-2 mt-3 flex min-h-[32px] flex-wrap items-center gap-2">{toolbar}</div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { data, loading, refreshing, error } = useDashboardStore();
  const { canWrite } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [revBackfilling, setRevBackfilling] = useState(false);
  const [revMode, setRevMode] = useState('all');
  const [revPlan, setRevPlan] = useState('total');
  const [revCurrency, setRevCurrency] = useState('usd');
  const [compareRange, setCompareRange] = useState('yesterday');
  const [range, setRange] = useState('14');
  const [activePlan, setActivePlan] = useState('total');
  const [cancelPlan, setCancelPlan] = useState('total');
  const [cancelMode, setCancelMode] = useState('requested');
  const [subsPlan, setSubsPlan] = useState('total');
  const [subsMode, setSubsMode] = useState('new');
  const staleTimerRef = useRef(null);
  const staleTriesRef = useRef(0);

  useEffect(() => {
    loadDashboard().catch(() => {});
    return () => {
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
    };
  }, []);

  // Caché `stale`: el Worker ya está recalculando en segundo plano; se
  // vuelve a pedir en unos segundos hasta que llegue la versión nueva.
  useEffect(() => {
    if (staleTimerRef.current) {
      clearTimeout(staleTimerRef.current);
      staleTimerRef.current = null;
    }
    if (data?.cache === 'stale' && staleTriesRef.current < STALE_REFETCH_MAX) {
      staleTriesRef.current += 1;
      staleTimerRef.current = setTimeout(() => loadDashboard({ force: true }).catch(() => {}), STALE_REFETCH_DELAY_MS);
    } else if (data && data.cache !== 'stale') {
      staleTriesRef.current = 0;
    }
  }, [data]);

  // Refrescar = todo: primero trae los eventos nuevos de Stripe (altas,
  // renovaciones, cancelaciones; 1-2 s al día) y después recalcula el stock
  // (activos, impago, trial: listado de Stripe + Firestore, ~15 s).
  function refresh() {
    setSyncMessage(null);
    refreshDashboard({ syncStripe: canWrite });
  }

  // Lanza un run del cron de eventos de Stripe (25 páginas máximo, ~15 s).
  // El backfill inicial de 30 días necesita 2 o 3 pulsaciones.
  async function syncStripe() {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await apiFetch('/api/backoffice/dashboard/sync-stripe', { method: 'POST' });
      const state = res?.state || {};
      setSyncMessage(state.lastCreated
        ? 'Stripe sincronizado al dia.'
        : `Historico cargado hasta ${formatDateTime((state.backfillCursor || 0) * 1000)}. Pulsa otra vez para seguir.`);
      await loadDashboard({ force: true });
    } catch (e) {
      setSyncMessage(`Error al sincronizar: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  }

  // Reconstruye las fotos diarias que faltan en el rango (Stripe + Firestore,
  // ~30-60 s). Solo rellena días sin foto; los reales no se tocan.
  async function backfillHistory() {
    setBackfilling(true);
    setSyncMessage(null);
    try {
      const res = await apiFetch(`/api/backoffice/dashboard/backfill-snapshots?days=${Number(range)}`, { method: 'POST' });
      setSyncMessage(res?.filled
        ? `Historico reconstruido desde Stripe: ${res.filled} dias (${formatDay(res.from)} a ${formatDay(res.to)})${res.replaced ? `, ${res.replaced} fotos del metodo antiguo sustituidas` : ''}.`
        : 'Todas las fotos del rango ya estan calculadas con Stripe.');
      await loadDashboard({ force: true });
    } catch (e) {
      setSyncMessage(`Error al reconstruir: ${e.message}`);
    } finally {
      setBackfilling(false);
    }
  }

  // Reconstruye la facturación diaria desde las facturas pagadas de Stripe.
  // El Worker va por tandas (12 páginas por llamada): se repite hasta done.
  async function backfillRevenue() {
    setRevBackfilling(true);
    setSyncMessage(null);
    try {
      let res = await apiFetch(`/api/backoffice/dashboard/backfill-revenue?days=${Number(range)}&restart=1`, { method: 'POST' });
      let rounds = 1;
      while (res && !res.done && rounds < 30) {
        setSyncMessage(`Facturacion: ${Math.round((res.progress || 0) * 100)}% (${res.invoices} facturas en esta tanda)…`);
        res = await apiFetch(`/api/backoffice/dashboard/backfill-revenue?days=${Number(range)}`, { method: 'POST' });
        rounds++;
      }
      setSyncMessage(res?.done ? 'Facturacion reconstruida desde las facturas de Stripe.' : 'Facturacion: reconstruccion incompleta, vuelve a pulsar.');
      await loadDashboard({ force: true });
    } catch (e) {
      setSyncMessage(`Error al reconstruir la facturacion: ${e.message}`);
    } finally {
      setRevBackfilling(false);
    }
  }

  const days = Number(range);
  const summary = data?.summary || null;
  const baseline = data?.comparisons?.[compareRange] || null;
  const snapshots = useMemo(() => lastDays(data?.snapshots || [], days), [data, days]);
  const flows = data?.flows?.series || null;

  const payingTotal = sumKeys(summary, ACTIVE_KEYS);
  const pastDueTotal = sumKeys(summary, PAST_DUE_KEYS);
  const activeTotal = payingTotal + pastDueTotal;
  const cancelingTotal = sumKeys(summary, CANCELING_KEYS);
  const baselineActive = baseline ? stripeActiveValue(baseline.summary, 'total') : undefined;
  const baselinePastDue = baseline ? sumKeys(baseline.summary, PAST_DUE_KEYS) : undefined;
  const baselineCanceling = baseline ? sumKeys(baseline.summary, CANCELING_KEYS) : undefined;
  // Lista "Suscripciones" de Stripe filtrada por estado activa: incluye las
  // programadas para cancelar (siguen activas hasta fin de periodo) y NO los
  // impagos (su estado es past_due).
  const subscriptionsTotal = payingTotal + cancelingTotal;
  const baselineSubscriptions = baseline
    ? sumKeys(baseline.summary, ACTIVE_KEYS) + sumKeys(baseline.summary, CANCELING_KEYS)
    : undefined;
  const baselineTrial = baseline ? n(baseline.summary?.trialing) : undefined;
  // summary es null hasta la primera carga: sin el `?.` el primer render
  // lanzaba un TypeError y React desmontaba toda la app (pantalla en negro).
  const fromStripe = summary?.source === 'stripe';
  const vsLabel = baseline ? `vs ${formatDay(baseline.day)}` : '';

  // Flujos de ayer y de hoy (las series terminan en el día de hoy, Madrid).
  const flowRow = (metric, offset) => {
    const rows = flows?.[metric] || [];
    return rows[rows.length - 1 - offset] || null;
  };
  const yesterday = {
    newSubs: flowValue(flowRow('new', 1), 'total'),
    renewed: flowValue(flowRow('renewed', 1), 'total'),
    cancelRequested: flowValue(flowRow('cancelRequested', 1), 'total'),
    ended: flowValue(flowRow('ended', 1), 'total')
  };
  const today = {
    newSubs: flowValue(flowRow('new', 0), 'total'),
    cancelRequested: flowValue(flowRow('cancelRequested', 0), 'total'),
    ended: flowValue(flowRow('ended', 0), 'total')
  };
  const yesterdayDay = flowRow('new', 1)?.date;

  // Calendario del rango elegido (días de Madrid que manda el Worker con los
  // flujos). Las gráficas de stock salen de las fotos diarias: los días sin
  // foto entran con null y dejan hueco, así el eje siempre cubre 14/30/90
  // días aunque el histórico sea más corto.
  const calendar = useMemo(() => {
    const fromFlows = (flows?.new || []).map((row) => row.date);
    const base = fromFlows.length ? fromFlows : (data?.snapshots || []).map((s) => s.day);
    return lastDays(base, days);
  }, [flows, data, days]);
  const snapshotByDay = useMemo(() => new Map((data?.snapshots || []).map((s) => [s.day, s])), [data]);
  const stockSeries = (valueOf) => calendar.map((day) => {
    const s = snapshotByDay.get(day);
    return { date: day, value: s ? valueOf(s) : null };
  });
  const lastValue = (points) => [...points].reverse().find((p) => p.value !== null && p.value !== undefined)?.value;
  const historyDays = calendar.filter((day) => snapshotByDay.has(day)).length;

  const activePoints = stockSeries((s) => stripeActiveValue(s, activePlan));
  const trialPoints = stockSeries((s) => n(s.trialing));
  const cancelPoints = cancelMode === 'scheduled'
    ? stockSeries((s) => stockValue(s, CANCELING_KEYS, cancelPlan))
    : lastDays(flows?.[cancelMode === 'ended' ? 'ended' : 'cancelRequested'] || [], days)
      .map((row) => ({ date: row.date, value: flowValue(row, cancelPlan) }));
  const subsPoints = lastDays(flows?.[subsMode === 'renewed' ? 'renewed' : 'new'] || [], days)
    .map((row) => ({ date: row.date, value: flowValue(row, subsPlan) }));
  const registrationPoints = lastDays(data?.registrations || [], days).map((row) => ({ date: row.date, value: n(row.total) }));

  // Facturación: métricas `revenue:<moneda>` (todo), `revenueNew:<moneda>` y
  // `revenueRenewed:<moneda>` en céntimos, por plan.
  const currencies = useMemo(() => {
    const found = Object.keys(flows || {})
      .filter((key) => key.startsWith('revenue:'))
      .map((key) => key.slice('revenue:'.length));
    return found.length ? found.sort() : ['usd'];
  }, [flows]);
  const activeCurrency = currencies.includes(revCurrency) ? revCurrency : currencies[0];
  const revenueMetric = revMode === 'new' ? 'revenueNew' : revMode === 'renewed' ? 'revenueRenewed' : 'revenue';
  const revenuePoints = lastDays(flows?.[`${revenueMetric}:${activeCurrency}`] || [], days)
    .map((row) => ({ date: row.date, value: flowValue(row, revPlan) / 100 }));
  // Sumas por moneda en una ventana de días (contando desde hoy hacia atrás).
  const revenueWindow = (metric, fromOffset, toOffset) => {
    const out = {};
    for (const currency of currencies) {
      const rows = flows?.[`${metric}:${currency}`] || [];
      let total = 0;
      for (let offset = fromOffset; offset <= toOffset; offset++) {
        const row = rows[rows.length - 1 - offset];
        total += flowValue(row, 'total');
      }
      out[currency] = total;
    }
    return out;
  };
  const revenueYesterday = revenueWindow('revenue', 1, 1);
  const revenueToday = revenueWindow('revenue', 0, 0);
  const revenue7 = revenueWindow('revenue', 0, 6);
  const revenue30 = revenueWindow('revenue', 0, 29);
  const revenueRange = revenueWindow('revenue', 0, days - 1);
  const revenueRangeNew = revenueWindow('revenueNew', 0, days - 1);
  const revenueRangeRenewed = revenueWindow('revenueRenewed', 0, days - 1);
  const revenueChartTotal = revenuePoints.reduce((acc, p) => acc + n(p.value), 0);
  const sumPoints = (points) => points.reduce((acc, p) => acc + n(p.value), 0);

  const flowState = data?.flows?.state;
  const flowsPending = !data?.flows?.ok || !flowState || flowState.mode !== 'live';
  const cacheLabel = refreshing
    ? 'Recalculando datos (Stripe + Firestore)… puedes cambiar de pestaña, sigue en segundo plano.'
    : data?.cache === 'stale'
    ? 'Datos de la ultima cache · actualizando en segundo plano…'
    : data?.computedAt
      ? `Stock calculado ${formatAge(data.computedAt)}`
      : '';

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold">Dashboard</h1>
          <p className="text-sm text-default-500">
            Suscriptores pagando, gente en trial, cancelaciones y altas de cada dia, por plan.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            size="sm"
            label="Comparar con"
            className="w-40"
            selectedKeys={[compareRange]}
            onSelectionChange={(keys) => setCompareRange(String(Array.from(keys)[0] || 'yesterday'))}
          >
            {COMPARE_OPTIONS.map(([key, label]) => <SelectItem key={key}>{label}</SelectItem>)}
          </Select>
          <Select
            size="sm"
            label="Graficas"
            className="w-32"
            selectedKeys={[range]}
            onSelectionChange={(keys) => setRange(String(Array.from(keys)[0] || '14'))}
          >
            {RANGE_OPTIONS.map(([key, label]) => <SelectItem key={key}>{label}</SelectItem>)}
          </Select>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{error}</div>
      )}
      {loading && !data && <div className="flex justify-center p-8"><Spinner /></div>}

      {summary && (
        <>
          {/* Refrescar va junto al estado de los datos, que es lo que actualiza.
              Las reconstrucciones son mantenimiento puntual: escondidas en un
              menu aparte para que no se confundan con actualizar. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
            <Button size="sm" color="primary" variant="flat" onPress={refresh} isLoading={refreshing}>
              Refrescar
            </Button>
            {cacheLabel && (
              <span className={refreshing || data.cache === 'stale' ? 'text-warning-600' : 'text-default-400'}>{cacheLabel}</span>
            )}
            {flowsPending ? (
              <span className="text-warning-600">
                {flowState?.mode === 'backfill'
                  ? `Historico de Stripe cargandose (hasta ${formatDateTime(flowState.backfillCursor)})…`
                  : 'Altas/cancelaciones por dia: pendientes del primer sincronizado con Stripe.'}
              </span>
            ) : (
              <span className="text-default-400">Stripe sincronizado {formatAge(flowState.syncedAt)}</span>
            )}
            {syncMessage && <span className="text-default-500">{syncMessage}</span>}
            {canWrite && data && (
              <div className="ml-auto flex items-center gap-2">
                {(syncing || backfilling || revBackfilling) && (
                  <span className="flex items-center gap-1.5 text-default-500">
                    <Spinner size="sm" />
                    {syncing ? 'Cargando historico de Stripe…' : backfilling ? 'Reconstruyendo fotos diarias…' : 'Reconstruyendo facturacion…'}
                  </span>
                )}
                <Dropdown placement="bottom-end">
                  <DropdownTrigger>
                    <Button size="sm" variant="light" className="text-default-500" isDisabled={syncing || backfilling || revBackfilling}>
                      Mantenimiento ▾
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="Mantenimiento del historico"
                    onAction={(key) => {
                      if (key === 'sync') syncStripe();
                      if (key === 'snapshots') backfillHistory();
                      if (key === 'revenue') backfillRevenue();
                    }}
                  >
                    <DropdownItem
                      key="sync"
                      description="Trae los eventos de Stripe pendientes (altas, cancelaciones, bajas). En produccion lo hace el cron cada 15 min."
                    >
                      Cargar historico de Stripe
                    </DropdownItem>
                    <DropdownItem
                      key="snapshots"
                      description={`Rehace las fotos diarias de activos/trial/programadas de los ultimos ${days} dias desde Stripe.`}
                      color={historyDays < days ? 'warning' : 'default'}
                    >
                      Reconstruir fotos diarias ({days} dias)
                    </DropdownItem>
                    <DropdownItem
                      key="revenue"
                      description={`Rehace la facturacion diaria de los ultimos ${days} dias desde las facturas pagadas. Tarda varios minutos.`}
                    >
                      Reconstruir facturacion ({days} dias)
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            )}
          </div>

          <DashboardSection
            title="Suscripciones de creadores"
            subtitle="Founders, Premium y Performance: suscriptores, cancelaciones, trial y facturacion. Sin agencias."
            accent={COLORS.active}
          >
          <div className="grid grid-cols-2 gap-y-6 md:grid-cols-3 xl:grid-cols-6 xl:divide-x xl:divide-default-200">
            <KpiStat
              title="Suscriptores activos"
              accent={COLORS.active}
              value={activeTotal}
              delta={<Delta value={activeTotal} baseline={baselineActive} label={vsLabel} />}
              breakdown={[
                ...planBreakdown(summary, ACTIVE_KEYS),
                ...(pastDueTotal > 0 ? [['en impago', pastDueTotal]] : [])
              ]}
              info={fromStripe
                ? 'Creadores con suscripcion activa sin cancelacion programada, mas los impagos en curso, contados por cliente como el Inicio de Stripe. No incluye las agencias: tienen su propio contador en el bloque Agencias (el Inicio de Stripe las suma a este numero).'
                : 'AVISO: Stripe no respondio; contado desde Firestore (puede no cuadrar con Stripe).'}
            />
            <KpiStat
              title="Suscripciones activas"
              accent={COLORS.renewed}
              value={subscriptionsTotal}
              delta={<Delta value={subscriptionsTotal} baseline={baselineSubscriptions} label={vsLabel} />}
              breakdown={planBreakdown(summary, ACTIVE_KEYS, CANCELING_KEYS)}
              info='Suscripciones de creadores activas (como la lista "Suscripciones" de Stripe filtrada por activas): incluye las que tienen la cancelacion programada y no incluye los impagos. Las de agencias van aparte, en su bloque.'
            />
            <KpiStat
              title="Cancelacion programada"
              accent={COLORS.cancel}
              value={cancelingTotal}
              delta={<Delta value={cancelingTotal} baseline={baselineCanceling} badWhenUp label={vsLabel} />}
              breakdown={planBreakdown(summary, CANCELING_KEYS)}
              info="Creadores que mantienen el acceso hasta el fin del periodo pagado y no renovaran. Sin agencias."
            />
            <KpiStat
              title="En impago"
              accent={COLORS.ended}
              value={pastDueTotal}
              delta={<Delta value={pastDueTotal} baseline={baselinePastDue} badWhenUp label={vsLabel} />}
              breakdown={planBreakdown(summary, PAST_DUE_KEYS)}
              info="Creadores con un cobro fallido en curso (past_due en Stripe). Siguen contando como activos hasta que Stripe los da de baja. Sin agencias."
            />
            <KpiStat
              title="En trial"
              accent={COLORS.trial}
              value={summary.trialing}
              delta={<Delta value={n(summary.trialing)} baseline={baselineTrial} label={vsLabel} />}
              info="Trial de 7 dias por vincular TikTok (app) o trial de Stripe, ahora mismo."
            />
            <div className="flex min-w-0 flex-col gap-1.5 px-5 first:pl-0 last:pr-0">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-default-500">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: COLORS.newSubs }} />
                <span className="truncate">Ayer{yesterdayDay ? ` · ${formatDay(yesterdayDay)}` : ''}</span>
                <InfoTip text="Movimientos de creadores del dia de ayer segun los eventos de Stripe: altas, clientes que decidieron cancelar, bajas (fin de acceso) y renovaciones cobradas. Sin agencias." />
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <MiniStat value={yesterday.newSubs} label="altas" color={COLORS.newSubs} />
                <MiniStat value={yesterday.cancelRequested} label="cancelan" color={COLORS.cancel} />
                <MiniStat value={yesterday.ended} label="bajas" color={COLORS.ended} />
                <MiniStat value={yesterday.renewed} label="renov." color={COLORS.renewed} />
              </div>
              <div className="text-[11px] text-default-400">
                Hoy: {fmt(today.newSubs)} altas · {fmt(today.cancelRequested)} cancelan · {fmt(today.ended)} bajas
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-y-6 border-t border-default-200 pt-5 md:grid-cols-3 md:divide-x md:divide-default-200 xl:grid-cols-5">
            <MoneyStat
              title="Facturado hoy"
              accent={COLORS.revenue}
              amounts={revenueToday}
              detail="Facturas pagadas hoy, hasta ahora"
              info="Importe cobrado en facturas pagadas de Stripe (amount_paid), por moneda y sin convertir. Los reembolsos no se restan. Solo creadores: lo cobrado a agencias va en su bloque."
            />
            <MoneyStat
              title="Facturado ayer"
              accent={COLORS.revenue}
              amounts={revenueYesterday}
              detail={yesterdayDay ? formatDay(yesterdayDay) : ''}
              info="Facturas de creadores pagadas ayer (dia de Madrid). Sin agencias."
            />
            <MoneyStat title="Ultimos 7 dias" accent={COLORS.revenue} amounts={revenue7} info="Facturas de creadores pagadas en los ultimos 7 dias, hoy incluido. Sin agencias." />
            <MoneyStat title="Ultimos 30 dias" accent={COLORS.revenue} amounts={revenue30} info="Facturas de creadores pagadas en los ultimos 30 dias, hoy incluido. Sin agencias." />
            <MoneyStat
              title={`Rango (${days} dias)`}
              accent={COLORS.revenue}
              amounts={revenueRange}
              detail={`Altas ${formatMoneyMap(revenueRangeNew)} · Renov. ${formatMoneyMap(revenueRangeRenewed)}`}
              info="Total de creadores del rango elegido en las graficas, separado en altas (nuevas y cambios de plan) y renovaciones. Sin agencias."
            />
          </div>
          </DashboardSection>

          <AgencyBillingSection agencies={data.agencies} canSeeIds={canWrite} baseline={baseline?.summary} vsLabel={vsLabel} />

          <DashboardSection
            title="Graficas por dia"
            subtitle="Total = creadores. Cada grafica se puede ver por plan, tambien Agencias."
            accent="#a1a1aa"
          >
          <div className="grid grid-cols-1 gap-y-8 xl:grid-cols-2 xl:divide-x xl:divide-default-200">
            <ChartSection
              title="Suscripciones activas"
              subtitle="Suscriptores pagando cada dia (foto diaria)"
              headline={lastValue(activePoints)}
              headlineLabel="hoy"
              toolbar={<PlanSwitch value={activePlan} onChange={setActivePlan} />}
            >
              <LineChart
                points={activePoints}
                color={COLORS.active}
                valueLabel="suscriptores activos"
                emptyText="Sin fotos diarias todavia: el historico se construye a partir de ahora."
              />
            </ChartSection>
            <ChartSection
              title="Suscriptores que cancelan"
              subtitle={cancelMode === 'scheduled'
                ? 'Con la cancelacion programada cada dia (foto diaria)'
                : cancelMode === 'ended'
                  ? 'Suscripciones que terminaron cada dia (fin de periodo o impago)'
                  : 'Clientes que decidieron cancelar cada dia'}
              headline={sumPoints(cancelPoints)}
              headlineLabel={cancelMode === 'scheduled' ? 'suma del periodo' : `total ${days} dias`}
              toolbar={(
                <>
                  <Tabs size="sm" selectedKey={cancelMode} onSelectionChange={(k) => setCancelMode(String(k))} aria-label="Tipo">
                    <Tab key="requested" title="Cancelaron" />
                    <Tab key="ended" title="Bajas" />
                    <Tab key="scheduled" title="Programadas" />
                  </Tabs>
                  <PlanSwitch value={cancelPlan} onChange={setCancelPlan} />
                </>
              )}
            >
              <LineChart
                points={cancelPoints}
                color={cancelMode === 'ended' ? COLORS.ended : COLORS.cancel}
                valueLabel={cancelMode === 'scheduled' ? 'con cancelacion programada' : cancelMode === 'ended' ? 'bajas' : 'cancelaciones'}
                emptyText={cancelMode === 'scheduled' ? 'Sin fotos diarias todavia.' : 'Sin datos de Stripe todavia.'}
              />
            </ChartSection>
          </div>

          {/* Con 90 dias las graficas necesitan ancho: dos por fila en vez de cuatro. */}
          <ChartRows perRow={days >= 90 ? 2 : 4}>
            <ChartSection
              title={subsMode === 'renewed' ? 'Renovaciones' : 'Altas'}
              subtitle={subsMode === 'renewed' ? 'Cobros de renovacion por dia' : 'Suscripciones nuevas por dia'}
              headline={sumPoints(subsPoints)}
              headlineLabel={`total ${days} dias`}
              toolbar={(
                <>
                  <Tabs size="sm" selectedKey={subsMode} onSelectionChange={(k) => setSubsMode(String(k))} aria-label="Tipo">
                    <Tab key="new" title="Nuevas" />
                    <Tab key="renewed" title="Renovadas" />
                  </Tabs>
                  <PlanSwitch value={subsPlan} onChange={setSubsPlan} />
                </>
              )}
            >
              <LineChart
                points={subsPoints}
                color={subsMode === 'renewed' ? COLORS.renewed : COLORS.newSubs}
                valueLabel={subsMode === 'renewed' ? 'renovaciones' : 'altas'}
                emptyText="Sin datos de Stripe todavia."
              />
            </ChartSection>
            <ChartSection
              title="Facturacion"
              subtitle={revMode === 'new' ? 'Cobrado por altas y cambios de plan cada dia' : revMode === 'renewed' ? 'Cobrado por renovaciones cada dia' : 'Cobrado cada dia (facturas pagadas)'}
              headline={revenueChartTotal}
              headlineLabel={`${CURRENCY_SYMBOLS[activeCurrency] || activeCurrency.toUpperCase()} · total ${days} dias`}
              info="Importes de Stripe en la moneda elegida, sin convertir ni restar reembolsos. Las altas incluyen los cambios de plan. Total = creadores; lo de agencias, con la opcion Agencias."
              toolbar={(
                <>
                  <Tabs size="sm" selectedKey={revMode} onSelectionChange={(k) => setRevMode(String(k))} aria-label="Tipo">
                    <Tab key="all" title="Todo" />
                    <Tab key="new" title="Altas" />
                    <Tab key="renewed" title="Renovaciones" />
                  </Tabs>
                  <PlanSwitch value={revPlan} onChange={setRevPlan} />
                  {currencies.length > 1 && (
                    <Select
                      aria-label="Moneda"
                      size="sm"
                      className="w-24"
                      disallowEmptySelection
                      selectedKeys={[activeCurrency]}
                      onSelectionChange={(keys) => setRevCurrency(String(Array.from(keys)[0] || 'usd'))}
                    >
                      {currencies.map((currency) => <SelectItem key={currency}>{currency.toUpperCase()}</SelectItem>)}
                    </Select>
                  )}
                </>
              )}
            >
              <LineChart
                points={revenuePoints}
                color={COLORS.revenue}
                valueLabel={`${activeCurrency.toUpperCase()} cobrados`}
                emptyText="Sin facturacion registrada: pulsa Reconstruir facturacion."
              />
            </ChartSection>
            <ChartSection
              title="En trial"
              subtitle="Usuarios en periodo de prueba cada dia (foto diaria)"
              headline={lastValue(trialPoints)}
              headlineLabel="hoy"
            >
              <LineChart points={trialPoints} color={COLORS.trial} valueLabel="en trial" emptyText="Sin fotos diarias todavia." />
            </ChartSection>
            <ChartSection
              title="Registros"
              subtitle="Cuentas creadas por dia"
              headline={sumPoints(registrationPoints)}
              headlineLabel={`total ${Math.min(days, 30)} dias`}
            >
              <LineChart points={registrationPoints} color={COLORS.registrations} valueLabel="registros" />
            </ChartSection>
          </ChartRows>
          </DashboardSection>

          <div className="border-t border-default-200 pt-3 text-xs text-default-400">
            Stock calculado: {formatDateTime(data.computedAt)} · documentos revisados: {fmt(data.scanned)}
            {data.notes?.length ? ` · ${data.notes.join(' ')}` : ''}
          </div>
        </>
      )}
    </div>
  );
}
